import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Rating,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { format } from 'date-fns';
import React, { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import StarRating from './StarRating';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { OTHER_SENTINEL } from '../ExperienceReviews/types';
import { capitalizeName } from '../../../shared/utils';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { lighten } from '@material-ui/core';
import { UserReview } from '../../types/review';

const SectionRow: React.FC<{ label: string; children: React.ReactNode }> = ({
    label,
    children,
}) => (
    <Stack sx={{ pb: 1 }} direction='row' spacing={1} alignItems='flex-start'>
        <Typography sx={{ minWidth: 160, fontSize: '14px' }}>
            {capitalizeName(label)}
        </Typography>
        <Box sx={{ flex: 1 }}>{children}</Box>
    </Stack>
);

export const highlightText = (text: string, keyword?: string) => {
    if (!keyword || !text) return text;

    const regex = new RegExp(`(${keyword})`, 'gi');
    const parts = text.split(regex);

    const theme = useTheme();

    return parts.map((part, index) => {
        if (part.toLowerCase() === keyword.toLowerCase()) {
            return (
                <Box
                    key={index}
                    component='span'
                    sx={{
                        backgroundColor: lighten(
                            theme.palette.policy.fair,
                            0.7,
                        ),
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '2px',
                    }}
                >
                    {part}
                </Box>
            );
        }
        return part;
    });
};
interface ReviewCardProps {
    review: UserReview;
    showCategoryRatings?: boolean;
    onShowMore?: (reviewId: number) => void;
    isInModal?: boolean;
    highlightKeyword?: string;
    replyActions?: React.ReactNode;
    hideHostReply?: boolean;
}

const ReviewCard: React.FC<ReviewCardProps> = ({
    review,
    onShowMore,
    isInModal = false,
    highlightKeyword,
    showCategoryRatings,
    replyActions,
    hideHostReply,
}) => {
    const theme = useTheme();
    const maxTextLength = 150;
    const isTextLong =
        review.reviewText && review.reviewText.length > maxTextLength;

    const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

    const handlePhotoClick = (index: number) => {
        setCurrentPhotoIndex(index);
        setPhotoGalleryOpen(true);
    };

    const handleGalleryClose = () => {
        setPhotoGalleryOpen(false);
    };

    const categoryRatings = Object.entries(review)
        .filter(([key]) =>
            [
                'access',
                'accuracy',
                'cleanliness',
                'communication',
                'value',
            ].includes(key),
        )
        .filter((category) => category[1]);

    return (
        <Card
            sx={{
                mb: 2,
                border: `1px solid ${theme.palette.custom.grey}`,
                borderRadius: '8px',
                boxShadow: 'none',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <CardContent
                sx={{
                    p: '20px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    display='flex'
                    alignItems='flex-start'
                    gap={2}
                    sx={{ flex: 1 }}
                >
                    <Box
                        flexGrow={1}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                        }}
                    >
                        <Box
                            display='flex'
                            justifyContent='space-between'
                            alignItems='flex-start'
                            mb={1}
                        >
                            <Box sx={{ display: 'flex', gap: 1.2 }}>
                                {review?.createdBy?.profilePicture ? (
                                    <Avatar
                                        src={
                                            review.createdBy.profilePicture.src
                                        }
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            height: '40px',
                                            width: '40px',
                                            borderRadius: '50%',
                                        }}
                                    >
                                        <AccountCircleIcon
                                            sx={{
                                                width: '110%',
                                                height: '110%',
                                                color: 'primary.main',
                                            }}
                                        />
                                    </Box>
                                )}
                                <Box>
                                    <Typography
                                        component={'div'}
                                        variant='h6'
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                        }}
                                    >
                                        {review?.isAnonymous
                                            ? 'Anonymous user'
                                            : review?.createdBy?.firstName ||
                                              'Guest user'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {format(
                                            new Date(review.createdAt),
                                            'MMM yyyy',
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                            <StarRating rating={review.rating} size='small' />
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Typography
                                variant='body3'
                                sx={{
                                    fontSize: '14px',
                                    lineHeight: 1.5,
                                    mb: 1,
                                }}
                            >
                                {isInModal || !isTextLong
                                    ? highlightText(
                                          review.reviewText,
                                          highlightKeyword,
                                      )
                                    : highlightText(
                                          `${review.reviewText.substring(
                                              0,
                                              maxTextLength,
                                          )}...`,
                                          highlightKeyword,
                                      )}
                            </Typography>
                            {!isInModal && isTextLong && onShowMore && (
                                <CustomButton
                                    variant='text'
                                    size='small'
                                    color='primary'
                                    onClick={() => onShowMore(review.id)}
                                    sx={{
                                        alignSelf: 'flex-start',
                                        fontSize: '12px',
                                        textTransform: 'none',
                                        p: 0,
                                        minHeight: 'auto',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    Read more
                                    <ArrowForwardIosIcon
                                        sx={{
                                            fontSize: '8px',
                                            ml: 0.1,
                                            mt: 0.3,
                                        }}
                                    />
                                </CustomButton>
                            )}
                        </Box>

                        {review.hostReplyText && !hideHostReply ? (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 1.5,
                                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                                    backgroundColor: lighten(
                                        theme.palette.primary.main,
                                        0.92,
                                    ),
                                    borderRadius: '0 8px 8px 0',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        mb: 0.5,
                                    }}
                                >
                                    Response from{' '}
                                    {review.hostReplyOwnerName ||
                                        review.hostRepliedBy?.firstName ||
                                        'host'}
                                </Typography>
                                {review.hostRepliedAt ? (
                                    <Typography
                                        sx={{
                                            fontSize: '11px',
                                            color: 'text.secondary',
                                            mb: 0.75,
                                        }}
                                    >
                                        {format(
                                            new Date(review.hostRepliedAt),
                                            'MMM yyyy',
                                        )}
                                    </Typography>
                                ) : null}
                                <Typography
                                    variant='body3'
                                    sx={{ fontSize: '14px', lineHeight: 1.5 }}
                                >
                                    {review.hostReplyText}
                                </Typography>
                            </Box>
                        ) : null}

                        {replyActions ? (
                            <Box sx={{ mt: 1.5 }}>{replyActions}</Box>
                        ) : null}

                        <Box sx={{ mt: 'auto' }}>
                            {review.photos?.length > 0 && (
                                <Box display='flex' gap={1} flexWrap='wrap'>
                                    {review.photos
                                        .slice(0, 3)
                                        .map((photo, index) => (
                                            <Box
                                                key={photo.id}
                                                component='img'
                                                src={photo.src}
                                                alt='Review photo'
                                                onClick={() =>
                                                    handlePhotoClick(index)
                                                }
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    borderRadius: '8px',
                                                    objectFit: 'cover',
                                                    border: `1px solid ${theme.palette.custom.grey}`,
                                                    cursor: 'pointer',
                                                    transition:
                                                        'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                        transform:
                                                            'scale(1.02)',
                                                        boxShadow:
                                                            '0 4px 12px rgba(0,0,0,0.15)',
                                                    },
                                                }}
                                            />
                                        ))}

                                    {review.photos.length > 3 && (
                                        <Box
                                            onClick={() => handlePhotoClick(3)}
                                            sx={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: '8px',
                                                backgroundColor:
                                                    theme.palette.custom
                                                        .lightGrey,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: `1px solid ${theme.palette.custom.grey}`,
                                                cursor: 'pointer',
                                                transition:
                                                    'transform 0.2s, box-shadow 0.2s',
                                                '&:hover': {
                                                    transform: 'scale(1.01)',
                                                    boxShadow:
                                                        '0 4px 12px rgba(0,0,0,0.15)',
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant='caption'
                                                sx={{
                                                    fontSize: '12px',
                                                }}
                                            >
                                                +{review.photos.length - 3}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {showCategoryRatings &&
                            categoryRatings.map((category: any) => {
                                const sectionData = category[1];
                                const name = category[0];

                                return (
                                    <Box sx={{ mt: 1 }}>
                                        <SectionRow label={`${name} rating`}>
                                            <Stack
                                                direction='row'
                                                alignItems='center'
                                                spacing={1}
                                            >
                                                <Rating
                                                    value={
                                                        sectionData?.rating || 0
                                                    }
                                                    readOnly
                                                />
                                                <Typography variant='body3'>
                                                    {sectionData?.rating || '-'}
                                                    /5
                                                </Typography>
                                            </Stack>
                                        </SectionRow>
                                        {(sectionData?.positives?.length || 0) >
                                            0 && (
                                            <SectionRow label='Positives'>
                                                <Stack
                                                    direction='row'
                                                    spacing={1}
                                                    flexWrap='wrap'
                                                    useFlexGap
                                                >
                                                    {sectionData.positives.map(
                                                        (positive: string) => (
                                                            <Chip
                                                                key={`p-${positive}`}
                                                                label={
                                                                    positive ===
                                                                    OTHER_SENTINEL
                                                                        ? 'Something else'
                                                                        : capitalizeName(
                                                                              positive,
                                                                          )
                                                                }
                                                                size='small'
                                                            />
                                                        ),
                                                    )}
                                                </Stack>
                                            </SectionRow>
                                        )}
                                        {(sectionData?.negatives?.length || 0) >
                                            0 && (
                                            <SectionRow label='Issues'>
                                                <Stack
                                                    direction='row'
                                                    spacing={1}
                                                    flexWrap='wrap'
                                                    useFlexGap
                                                >
                                                    {sectionData.negatives.map(
                                                        (negative: string) => (
                                                            <Chip
                                                                key={`n-${negative}`}
                                                                label={
                                                                    negative ===
                                                                    OTHER_SENTINEL
                                                                        ? 'Something else'
                                                                        : capitalizeName(
                                                                              negative,
                                                                          )
                                                                }
                                                                size='small'
                                                            />
                                                        ),
                                                    )}
                                                </Stack>
                                            </SectionRow>
                                        )}
                                        {sectionData?.otherTextPositive?.trim?.() && (
                                            <SectionRow label='Something else (positive)'>
                                                <Typography variant='body3'>
                                                    {
                                                        sectionData.otherTextPositive
                                                    }
                                                </Typography>
                                            </SectionRow>
                                        )}
                                        {sectionData?.otherTextNegative?.trim?.() && (
                                            <SectionRow label='Something else (issue)'>
                                                <Typography variant='body3'>
                                                    {
                                                        sectionData.otherTextNegative
                                                    }
                                                </Typography>
                                            </SectionRow>
                                        )}
                                        <Divider sx={{ mt: 1.5 }} />
                                    </Box>
                                );
                            })}
                    </Box>
                </Box>
            </CardContent>
            <Lightbox
                index={currentPhotoIndex}
                open={photoGalleryOpen}
                close={handleGalleryClose}
                slides={review.photos}
                plugins={[Fullscreen, Thumbnails, Zoom]}
                toolbar={{
                    buttons: ['close'],
                }}
                zoom={{
                    maxZoomPixelRatio: 50,
                    zoomInMultiplier: 2,
                    doubleTapDelay: 0,
                    doubleClickDelay: 0,
                    keyboardMoveDistance: 0,
                    wheelZoomDistanceFactor: 0,
                    pinchZoomDistanceFactor: 0,
                    scrollToZoom: false,
                }}
                carousel={{ preload: 1, padding: 0, imageFit: 'contain' }}
            />
        </Card>
    );
};

export default ReviewCard;
